import type { CareerOfferDestinationContext } from './destination.ts';
import type { CareerOfferTechnology } from './events.ts';

export type CareerOfferRouteContext = Partial<
	Omit<CareerOfferDestinationContext, 'routing'>
> & {
	routing: CareerOfferDestinationContext['routing'];
};

const routingValues = new Set<CareerOfferDestinationContext['routing']>([
	'manual',
	'primary_language_auto',
	'direct',
]);
const entrypointValues = new Set<CareerOfferDestinationContext['entrypoint']>([
	'selector',
	'promo',
	'menu',
	'direct',
]);
const sourceValues = new Set<
	NonNullable<CareerOfferDestinationContext['source']>
>(['problems_card', 'courses_menu']);
const placementValues = new Set<
	NonNullable<CareerOfferDestinationContext['placement']>
>([
	'problems_popular_courses',
	'courses_menu_desktop',
	'courses_menu_mobile',
	'auth_system_banner',
	'community_courses_menu',
	'community_footer',
	'problems_footer',
]);

const readEnum = <T extends string>(
	searchParams: URLSearchParams,
	key: string,
	values: Set<T>,
): T | undefined => {
	const value = searchParams.get(key);
	return value && values.has(value as T) ? (value as T) : undefined;
};

const readText = (searchParams: URLSearchParams, key: string) => {
	const value = searchParams.get(key)?.trim();
	return value ? value.slice(0, 200) : undefined;
};

export const getCareerOfferRouteContext = (
	searchParams: URLSearchParams,
): CareerOfferRouteContext => ({
	routing:
		readEnum(searchParams, 'career_offer_routing', routingValues) ?? 'direct',
	...(readEnum(searchParams, 'career_offer_entrypoint', entrypointValues) && {
		entrypoint: readEnum(
			searchParams,
			'career_offer_entrypoint',
			entrypointValues,
		),
	}),
	...(readEnum(searchParams, 'career_offer_source', sourceValues) && {
		source: readEnum(searchParams, 'career_offer_source', sourceValues),
	}),
	...(readEnum(searchParams, 'career_offer_placement', placementValues) && {
		placement: readEnum(
			searchParams,
			'career_offer_placement',
			placementValues,
		),
	}),
	...(readText(searchParams, 'career_offer_campaign') && {
		campaign: readText(searchParams, 'career_offer_campaign'),
	}),
	...(readText(searchParams, 'career_offer_creative') && {
		creative: readText(searchParams, 'career_offer_creative'),
	}),
});

export const getCareerOfferTechnology = (
	pathname: string,
): CareerOfferTechnology | undefined => {
	const technology = pathname.split('/').filter(Boolean)[1];
	return technology === 'java' ||
		technology === 'go' ||
		technology === 'python' ||
		technology === 'other'
		? technology
		: undefined;
};
